// AI Build Lab Email Sender — dedicated, mobile-first admin tool used the
// night of the live lab. PIN-gated. Mounted at /lab-sender (NOT under /admin).
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Loader2, Lock, Send, Calendar, FlaskConical, RefreshCw, Mail,
  CheckCircle2, XCircle, Clock, Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labFetch = (path, pin, init = {}) =>
  axios({
    url: `${API}/lab-sender${path}`,
    method: init.method || "GET",
    headers: { "X-Admin-Pin": pin, "Content-Type": "application/json" },
    data: init.body,
  }).then((r) => r.data).catch((e) => {
    const msg = e.response?.data?.detail || e.message;
    throw new Error(msg);
  });

export default function LabSender() {
  const [pin, setPin] = useState(sessionStorage.getItem("lab_pin") || "");
  const [authed, setAuthed] = useState(false);
  const [meta, setMeta] = useState(null);
  const [checking, setChecking] = useState(false);

  // Form state
  const [emailsRaw, setEmailsRaw] = useState("");
  const [labLink, setLabLink] = useState("");
  const [workbookLink, setWorkbookLink] = useState("");
  const [faqLink, setFaqLink] = useState("");
  const [followupHubLink, setFollowupHubLink] = useState("https://donjawilliams.com/build-lab-follow-up");
  const [emergentLink, setEmergentLink] = useState("https://app.emergent.sh/?via=donja");
  const [testEmail, setTestEmail] = useState("");
  const [force, setForce] = useState(false);

  // UI state
  const [busyKickoff, setBusyKickoff] = useState(false);
  const [busySchedule, setBusySchedule] = useState(false);
  const [busyFollowup, setBusyFollowup] = useState(false);
  const [busyTest, setBusyTest] = useState(false);
  const [busyRetry, setBusyRetry] = useState(false);
  const [status, setStatus] = useState(null);

  const parsedEmails = useMemo(() => {
    const parts = (emailsRaw || "").split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const valid = new Set();
    const invalid = [];
    const re = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    parts.forEach((p) => (re.test(p) ? valid.add(p) : invalid.push(p)));
    return { valid: Array.from(valid), invalid };
  }, [emailsRaw]);

  // ---- Auth ----
  async function tryLogin(e) {
    e?.preventDefault?.();
    if (!pin.trim()) { toast.error("Enter your PIN."); return; }
    setChecking(true);
    try {
      const m = await labFetch("/auth", pin.trim(), { method: "POST" });
      sessionStorage.setItem("lab_pin", pin.trim());
      setMeta(m);
      setAuthed(true);
      toast.success("Welcome — sender unlocked.");
      refresh();
    } catch (e) {
      toast.error(e.message || "Invalid PIN");
    } finally {
      setChecking(false);
    }
  }

  function logout() {
    sessionStorage.removeItem("lab_pin");
    setAuthed(false);
    setMeta(null);
    setPin("");
  }

  async function refresh() {
    try {
      const s = await labFetch("/status", pin);
      setStatus(s);
    } catch (e) {
      if (String(e.message).includes("Invalid PIN")) logout();
    }
  }

  // On mount: if a PIN exists in sessionStorage, try silent re-auth.
  useEffect(() => {
    if (!sessionStorage.getItem("lab_pin")) return;
    (async () => {
      try {
        const m = await labFetch("/auth", sessionStorage.getItem("lab_pin"), { method: "POST" });
        setMeta(m);
        setAuthed(true);
        const s = await labFetch("/status", sessionStorage.getItem("lab_pin"));
        setStatus(s);
      } catch {
        sessionStorage.removeItem("lab_pin");
        setAuthed(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh meta + status every 20s so the button correctly flips from
  // "Schedule for 7:05 PM ET" → "Send Workbook + FAQ Now" once that time passes,
  // even if the admin has been sitting on the page for a while.
  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => refresh(), 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, pin]);

  // Tick state every 5s so the past705 boolean below recomputes in real time
  // (we compare to LIVE Date.now() — not the stale meta.now_eastern from login).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [authed]);

  // ---- Actions ----
  async function sendKickoff() {
    if (parsedEmails.valid.length === 0) { toast.error("Paste at least one valid email."); return; }
    if (!labLink.trim()) { toast.error("Add the lab link."); return; }
    if (!window.confirm(`Send the KICKOFF email to ${parsedEmails.valid.length} recipient${parsedEmails.valid.length === 1 ? "" : "s"} right now?${force ? "\n\nForce Resend is ON — recipients who already received this email will get it again." : ""}`)) return;
    setBusyKickoff(true);
    try {
      const res = await labFetch("/send-kickoff", pin, {
        method: "POST",
        body: { emails_raw: emailsRaw, lab_link: labLink.trim(), force },
      });
      toast.success(`Kickoff: sent ${res.sent}, failed ${res.failed}${res.skipped_dupe?.length ? `, skipped ${res.skipped_dupe.length} duplicate${res.skipped_dupe.length === 1 ? "" : "s"}` : ""}.`);
      refresh();
    } catch (e) {
      toast.error(e.message || "Send failed");
    } finally { setBusyKickoff(false); }
  }

  async function scheduleWorkbook(sendImmediate = false) {
    if (parsedEmails.valid.length === 0) { toast.error("Paste at least one valid email."); return; }
    if (!workbookLink.trim() || !faqLink.trim()) { toast.error("Add both the workbook and FAQ links."); return; }
    if (sendImmediate && !window.confirm(`Send the WORKBOOK + FAQ email NOW to ${parsedEmails.valid.length} recipient${parsedEmails.valid.length === 1 ? "" : "s"}?`)) return;
    setBusySchedule(true);
    try {
      const res = await labFetch("/schedule-workbook", pin, {
        method: "POST",
        body: {
          emails_raw: emailsRaw,
          workbook_link: workbookLink.trim(),
          faq_link: faqLink.trim(),
          send_immediately: sendImmediate,
          force,
        },
      });
      if (res.warning === "past_scheduled_time") {
        // 7:05 PM ET has passed — auto-offer to send right now instead of leaving the user stuck.
        const confirmNow = window.confirm(
          `7:05 PM ET has already passed. Send the Workbook + FAQ email to ${parsedEmails.valid.length} recipient${parsedEmails.valid.length === 1 ? "" : "s"} RIGHT NOW instead?`
        );
        if (confirmNow) {
          const res2 = await labFetch("/schedule-workbook", pin, {
            method: "POST",
            body: {
              emails_raw: emailsRaw,
              workbook_link: workbookLink.trim(),
              faq_link: faqLink.trim(),
              send_immediately: true,
              force,
            },
          });
          toast.success(`Sent: ${res2.sent} sent, ${res2.failed} failed${res2.skipped_dupe?.length ? `, ${res2.skipped_dupe.length} skipped` : ""}.`);
        } else {
          toast.warning("Not sent. Click 'Send Workbook + FAQ Now' when ready.");
        }
        refresh();
        return;
      }
      if (res.mode === "scheduled") {
        toast.success(`Scheduled for ${new Date(res.scheduled_at_eastern).toLocaleString("en-US", { timeZone: "America/New_York" })}. ${res.recipients} recipients queued.`);
      } else {
        toast.success(`Sent now: ${res.sent} sent, ${res.failed} failed${res.skipped_dupe?.length ? `, ${res.skipped_dupe.length} skipped` : ""}.`);
      }
      refresh();
    } catch (e) {
      toast.error(e.message || "Schedule failed");
    } finally { setBusySchedule(false); }
  }

  async function sendTest() {
    if (!testEmail.trim()) { toast.error("Add your test email."); return; }
    if (!labLink.trim() || !workbookLink.trim() || !faqLink.trim()) {
      toast.error("Fill the lab link + workbook + FAQ first so the test reflects the real send.");
      return;
    }
    setBusyTest(true);
    try {
      const res = await labFetch("/send-test", pin, {
        method: "POST",
        body: { to_email: testEmail.trim(), lab_link: labLink.trim(), workbook_link: workbookLink.trim(), faq_link: faqLink.trim(), followup_hub_link: followupHubLink.trim() },
      });
      if (res.ok) {
        toast.success(`All test emails sent to ${res.test_email}. Check your inbox.`);
      } else {
        toast.error(`Some tests failed: ${res.results.filter(r => !r.ok).map(r => r.email_type + ' - ' + r.error).join('; ')}`);
      }
      refresh();
    } catch (e) {
      toast.error(e.message || "Test failed");
    } finally { setBusyTest(false); }
  }

  async function sendFollowupHub() {
    if (parsedEmails.valid.length === 0) { toast.error("Paste at least one valid email."); return; }
    const hub = followupHubLink.trim();
    if (!hub) { toast.error("Add the follow-up hub URL."); return; }
    const worksheet = workbookLink.trim();
    const faq = faqLink.trim();
    const emergent = emergentLink.trim();
    if (!worksheet || !faq) { toast.error("Add the Worksheet + FAQ links above — they're included in the email."); return; }
    if (!window.confirm(`Send the FOLLOW-UP HUB email to ${parsedEmails.valid.length} recipient${parsedEmails.valid.length === 1 ? "" : "s"} right now?\n\nIncludes:\n• Worksheet button\n• FAQ button\n• Emergent affiliate signup\n• Follow-Up Hub CTA${force ? "\n\nForce Resend is ON — recipients who already received this email will get it again." : ""}`)) return;
    setBusyFollowup(true);
    try {
      const res = await labFetch("/send-followup-hub", pin, {
        method: "POST",
        body: {
          emails_raw: emailsRaw,
          followup_hub_link: hub,
          worksheet_link: worksheet,
          faq_link: faq,
          emergent_affiliate_link: emergent,
          force,
        },
      });
      toast.success(`Follow-Up Hub: sent ${res.sent}, failed ${res.failed}${res.skipped_dupe?.length ? `, skipped ${res.skipped_dupe.length} duplicate${res.skipped_dupe.length === 1 ? "" : "s"}` : ""}.`);
      refresh();
    } catch (e) {
      toast.error(e.message || "Send failed");
    } finally { setBusyFollowup(false); }
  }

  async function scheduleFollowupHub(forcedImmediate = false) {
    if (parsedEmails.valid.length === 0) { toast.error("Paste at least one valid email."); return; }
    const hub = followupHubLink.trim();
    if (!hub) { toast.error("Add the follow-up hub URL."); return; }
    const worksheet = workbookLink.trim();
    const faq = faqLink.trim();
    if (!worksheet || !faq) { toast.error("Add the Worksheet + FAQ links above — they're included in the email."); return; }
    const target = meta?.next_7am_eastern ? new Date(meta.next_7am_eastern).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "long", hour: "numeric", minute: "2-digit", hour12: true }) : "7:00 AM ET";
    if (!forcedImmediate && !window.confirm(`Schedule the Follow-Up Hub email to ${parsedEmails.valid.length} recipient${parsedEmails.valid.length === 1 ? "" : "s"} for ${target}?\n\nIt will auto-send at that time. You don't need to keep this page open.`)) return;
    setBusyFollowup(true);
    try {
      const res = await labFetch("/schedule-followup-hub", pin, {
        method: "POST",
        body: {
          emails_raw: emailsRaw,
          followup_hub_link: hub,
          worksheet_link: worksheet,
          faq_link: faq,
          emergent_affiliate_link: emergentLink.trim(),
          scheduled_at_eastern: "",  // backend defaults to next 7am ET
          force,
          send_immediately: forcedImmediate,
        },
      });
      if (res.warning === "past_scheduled_time") {
        const confirmNow = window.confirm(`That time has already passed. Send the Follow-Up Hub email to ${parsedEmails.valid.length} recipient${parsedEmails.valid.length === 1 ? "" : "s"} RIGHT NOW instead?`);
        if (confirmNow) await scheduleFollowupHub(true);
        return;
      }
      if (res.mode === "scheduled") {
        toast.success(`Scheduled for ${new Date(res.scheduled_at_eastern).toLocaleString("en-US", { timeZone: "America/New_York" })} — ${res.recipients} recipients queued.`);
      } else if (res.mode === "sent_immediately") {
        toast.success(`Sent now: ${res.sent} sent, ${res.failed} failed${res.skipped_dupe?.length ? `, ${res.skipped_dupe.length} skipped` : ""}.`);
      }
      refresh();
    } catch (e) {
      toast.error(e.message || "Schedule failed");
    } finally { setBusyFollowup(false); }
  }

  async function retryFailed(emailType) {
    if (!window.confirm(`Retry all failed sends for "${emailType}"?\n\nMake sure the relevant link fields above are still filled.`)) return;
    if (emailType === "kickoff_email" && !labLink.trim()) { toast.error("Add the lab link first."); return; }
    if (emailType === "workbook_faq_email" && (!workbookLink.trim() || !faqLink.trim())) { toast.error("Add the workbook + FAQ links first."); return; }
    if (emailType === "followup_hub_email" && !followupHubLink.trim()) { toast.error("Add the follow-up hub URL first."); return; }
    setBusyRetry(true);
    try {
      const qs = new URLSearchParams({
        email_type: emailType,
        lab_link: labLink.trim(),
        workbook_link: workbookLink.trim(),
        faq_link: faqLink.trim(),
        followup_hub_link: followupHubLink.trim(),
      });
      const res = await labFetch(`/retry-failed?${qs.toString()}`, pin, { method: "POST" });
      if (res.retried === 0 && res.detail) {
        toast.info(res.detail);
      } else {
        toast.success(`Retry: ${res.sent} succeeded, ${res.failed} failed.`);
      }
      refresh();
    } catch (e) {
      toast.error(e.message || "Retry failed");
    } finally { setBusyRetry(false); }
  }

  async function cancelSchedule(sid) {
    if (!window.confirm("Cancel this scheduled workbook send?")) return;
    try {
      await labFetch(`/scheduled/${sid}`, pin, { method: "DELETE" });
      toast.success("Cancelled.");
      refresh();
    } catch (e) {
      toast.error(e.message);
    }
  }

  // ---- Render ----
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center px-4">
        <Card className="w-full max-w-sm border-[#E5E0D2] bg-white rounded-2xl shadow-sm" data-testid="lab-sender-login-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-[#1A2DD7]/10 flex items-center justify-center">
              <Lock size={20} className="text-[#1A2DD7]" />
            </div>
            <CardTitle className="font-serif tracking-[-0.02em] text-2xl">AI Build Lab Email Sender</CardTitle>
            <CardDescription className="text-[#5b5b56]">Enter your admin PIN to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={tryLogin} className="space-y-3">
              <Input
                type="password"
                placeholder="PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                autoFocus
                data-testid="lab-pin-input"
                className="bg-[#F5F1E8] border-[#E5E0D2] focus-visible:ring-[#1A2DD7] focus-visible:ring-offset-0 h-11"
              />
              <Button
                type="submit"
                disabled={checking}
                data-testid="lab-pin-submit"
                className="w-full rounded-full h-11 bg-[#1A2DD7] hover:bg-[#1A2DD7]/90 text-white font-semibold"
              >
                {checking ? <Loader2 size={14} className="animate-spin" /> : "Unlock"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sendingTime = meta?.scheduled_send_eastern ? new Date(meta.scheduled_send_eastern) : null;
  const nowET = meta?.now_eastern ? new Date(meta.now_eastern) : new Date();
  // Use LIVE wall-clock for the past-7:05 check so the button switches in real time
  // (the meta's now_eastern is only fresh at login; we re-poll every 20s but this
  // makes the swap instantaneous via the 5-second tick).
  const past705 = sendingTime && Date.now() >= sendingTime.getTime();
  const kickoffSummary = status?.summary?.kickoff_email || { sent: 0, failed: 0 };
  const workbookSummary = status?.summary?.workbook_faq_email || { sent: 0, failed: 0 };
  const followupSummary = status?.summary?.followup_hub_email || { sent: 0, failed: 0 };

  return (
    <div className="min-h-screen bg-[#F5F1E8] text-[#0A0A0A] py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5b5b56] font-mono mb-1">Lab Night Tool</p>
            <h1 className="font-serif tracking-[-0.025em] text-3xl sm:text-4xl">AI Build Lab Email Sender</h1>
            <p className="text-sm text-[#5b5b56] mt-1">
              Now in NY: <strong>{nowET.toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", minute: "2-digit", hour12: true })}</strong>
              {sendingTime && (<>{" "}· Workbook auto-send: <strong>{sendingTime.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true })}</strong></>)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} className="rounded-full h-9" data-testid="lab-refresh"><RefreshCw size={12} className="mr-1.5" />Refresh</Button>
            <Button variant="outline" size="sm" onClick={logout} className="rounded-full h-9" data-testid="lab-logout"><Shield size={12} className="mr-1.5" />Sign out</Button>
          </div>
        </div>

        {!meta?.resend_ready && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="pt-5"><p className="text-sm text-red-700"><strong>Setup error:</strong> RESEND_API_KEY is not configured on the server. Nothing will send until that is fixed.</p></CardContent>
          </Card>
        )}

        {/* Recipients + links */}
        <Card className="border-[#E5E0D2] bg-white rounded-2xl">
          <CardHeader>
            <CardTitle className="font-serif tracking-[-0.02em] text-xl flex items-center gap-2"><Mail size={16} className="text-[#1A2DD7]" /> Recipients & Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#5b5b56] font-mono mb-1.5">Recipient emails</label>
              <Textarea
                rows={5}
                value={emailsRaw}
                onChange={(e) => setEmailsRaw(e.target.value)}
                placeholder="Paste one per line or comma-separated. Duplicates auto-removed."
                data-testid="lab-emails-input"
                className="bg-[#F5F1E8] border-[#E5E0D2] focus-visible:ring-[#1A2DD7] focus-visible:ring-offset-0 font-mono text-sm"
              />
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="text-[#5b5b56]" data-testid="lab-emails-count">{parsedEmails.valid.length} unique valid email{parsedEmails.valid.length === 1 ? "" : "s"}</span>
                {parsedEmails.invalid.length > 0 && (<span className="text-red-600">{parsedEmails.invalid.length} skipped (invalid format)</span>)}
              </div>
            </div>

            <FieldRow label="Lab / Zoom link" value={labLink} setValue={setLabLink} placeholder="https://us02web.zoom.us/j/..." testid="lab-link" />
            <FieldRow label="Workbook link" value={workbookLink} setValue={setWorkbookLink} placeholder="https://... (PDF, Notion, Drive)" testid="lab-workbook" />
            <FieldRow label="FAQ guide link" value={faqLink} setValue={setFaqLink} placeholder="https://... (PDF, Notion, Drive)" testid="lab-faq" />
            <FieldRow label="Follow-Up Hub link (post-lab)" value={followupHubLink} setValue={setFollowupHubLink} placeholder="https://donjawilliams.com/build-lab-follow-up" testid="lab-followup-hub" />
            <FieldRow label="Emergent affiliate link (post-lab email)" value={emergentLink} setValue={setEmergentLink} placeholder="https://app.emergent.sh/?via=donja" testid="lab-emergent-affiliate" />

            <label className="flex items-center gap-2 text-sm text-[#0A0A0A] cursor-pointer">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="lab-force-checkbox" className="rounded" />
              <span>Force Resend (override per-recipient duplicate guard)</span>
            </label>
          </CardContent>
        </Card>

        {/* Action cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border-[#E5E0D2] bg-white rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif tracking-[-0.02em] text-base flex items-center gap-2"><Send size={14} className="text-[#1A2DD7]" /> 1. Kickoff Email</CardTitle>
              <CardDescription className="text-xs">Lab link + 7 PM ET reminder. Sends immediately.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-xs text-[#5b5b56] flex gap-3">
                <span><CheckCircle2 size={11} className="inline mr-0.5 text-emerald-600" />{kickoffSummary.sent} sent</span>
                <span><XCircle size={11} className="inline mr-0.5 text-red-500" />{kickoffSummary.failed} failed</span>
              </div>
              <Button onClick={sendKickoff} disabled={busyKickoff || !meta?.resend_ready} data-testid="lab-send-kickoff" className="w-full rounded-full h-11 bg-[#1A2DD7] hover:bg-[#1A2DD7]/90 text-white font-semibold">
                {busyKickoff ? <Loader2 size={14} className="animate-spin" /> : "Send Kickoff Email Now"}
              </Button>
              {kickoffSummary.failed > 0 && (
                <Button variant="outline" onClick={() => retryFailed("kickoff_email")} disabled={busyRetry} data-testid="lab-retry-kickoff" className="w-full rounded-full h-9 text-xs">
                  <RefreshCw size={11} className="mr-1.5" />Retry {kickoffSummary.failed} failed
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="border-[#E5E0D2] bg-white rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif tracking-[-0.02em] text-base flex items-center gap-2"><Calendar size={14} className="text-[#1A2DD7]" /> 2. Workbook + FAQ</CardTitle>
              <CardDescription className="text-xs">
                {past705 ? <span className="text-amber-700"><Clock size={11} className="inline mr-0.5" />7:05 PM ET has passed — use Send now.</span> : <>Scheduled for <strong>{sendingTime?.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true })} ET</strong>.</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-xs text-[#5b5b56] flex gap-3">
                <span><CheckCircle2 size={11} className="inline mr-0.5 text-emerald-600" />{workbookSummary.sent} sent</span>
                <span><XCircle size={11} className="inline mr-0.5 text-red-500" />{workbookSummary.failed} failed</span>
              </div>
              {past705 ? (
                <Button onClick={() => scheduleWorkbook(true)} disabled={busySchedule || !meta?.resend_ready} data-testid="lab-send-workbook-now" className="w-full rounded-full h-11 bg-[#1A2DD7] hover:bg-[#1A2DD7]/90 text-white font-semibold">
                  {busySchedule ? <Loader2 size={14} className="animate-spin" /> : "Send Workbook + FAQ Now"}
                </Button>
              ) : (
                <Button onClick={() => scheduleWorkbook(false)} disabled={busySchedule || !meta?.resend_ready} data-testid="lab-schedule-workbook" className="w-full rounded-full h-11 bg-[#1A2DD7] hover:bg-[#1A2DD7]/90 text-white font-semibold">
                  {busySchedule ? <Loader2 size={14} className="animate-spin" /> : "Schedule for 7:05 PM ET"}
                </Button>
              )}
              {workbookSummary.failed > 0 && (
                <Button variant="outline" onClick={() => retryFailed("workbook_faq_email")} disabled={busyRetry} data-testid="lab-retry-workbook" className="w-full rounded-full h-9 text-xs">
                  <RefreshCw size={11} className="mr-1.5" />Retry {workbookSummary.failed} failed
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="border-[#E5E0D2] bg-white rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif tracking-[-0.02em] text-base flex items-center gap-2"><Send size={14} className="text-[#1A2DD7]" /> 3. Follow-Up Hub Email</CardTitle>
              <CardDescription className="text-xs">Photo + thank-you + Worksheet + FAQ + Emergent + Hub. Send now or schedule for 7 AM ET.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-xs text-[#5b5b56] flex gap-3">
                <span><CheckCircle2 size={11} className="inline mr-0.5 text-emerald-600" />{followupSummary.sent} sent</span>
                <span><XCircle size={11} className="inline mr-0.5 text-red-500" />{followupSummary.failed} failed</span>
              </div>
              <Button onClick={() => scheduleFollowupHub()} disabled={busyFollowup || !meta?.resend_ready} data-testid="lab-schedule-followup-hub" className="w-full rounded-full h-11 bg-[#1A2DD7] hover:bg-[#1A2DD7]/90 text-white font-semibold">
                {busyFollowup ? <Loader2 size={14} className="animate-spin" /> : `Schedule for ${meta?.next_7am_eastern ? new Date(meta.next_7am_eastern).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "7:00 AM ET"}`}
              </Button>
              <Button variant="outline" onClick={sendFollowupHub} disabled={busyFollowup || !meta?.resend_ready} data-testid="lab-send-followup-hub" className="w-full rounded-full h-9 text-xs">
                Send right now instead
              </Button>
              {followupSummary.failed > 0 && (
                <Button variant="outline" onClick={() => retryFailed("followup_hub_email")} disabled={busyRetry} data-testid="lab-retry-followup" className="w-full rounded-full h-9 text-xs">
                  <RefreshCw size={11} className="mr-1.5" />Retry {followupSummary.failed} failed
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Test */}
        <Card className="border-[#E5E0D2] bg-white rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-serif tracking-[-0.02em] text-base flex items-center gap-2"><FlaskConical size={14} className="text-[#1A2DD7]" /> Send Test Email (both templates)</CardTitle>
            <CardDescription className="text-xs">Sends both templates to a single test address so you can preview before blasting.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              <Input type="email" placeholder="your-test@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="lab-test-email" className="bg-[#F5F1E8] border-[#E5E0D2] focus-visible:ring-[#1A2DD7] focus-visible:ring-offset-0 flex-1 min-w-[200px]" />
              <Button onClick={sendTest} disabled={busyTest || !meta?.resend_ready} data-testid="lab-send-test" className="rounded-full h-10 px-5 bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white">
                {busyTest ? <Loader2 size={14} className="animate-spin" /> : "Send Test"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pending schedules */}
        {status?.scheduled?.length > 0 && (
          <Card className="border-[#E5E0D2] bg-white rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif tracking-[-0.02em] text-base flex items-center gap-2"><Clock size={14} className="text-[#1A2DD7]" /> Pending schedules</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {status.scheduled.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#F5F1E8] border border-[#E5E0D2]" data-testid={`lab-pending-${s.id}`}>
                  <div className="text-sm">
                    <div className="font-medium">{s.email_type === "workbook_faq_email" ? "Workbook + FAQ Guide" : s.email_type === "followup_hub_email" ? "Follow-Up Hub Email" : s.email_type}</div>
                    <div className="text-xs text-[#5b5b56] mt-0.5">{new Date(s.scheduled_at_eastern).toLocaleString("en-US", { timeZone: "America/New_York" })} · {s.emails?.length || 0} recipients</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => cancelSchedule(s.id)} className="rounded-full h-8 px-3 text-xs">Cancel</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Recent send log */}
        <Card className="border-[#E5E0D2] bg-white rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-serif tracking-[-0.02em] text-base">Recent send log</CardTitle>
            <CardDescription className="text-xs">Most recent 50 deliveries · auto-refresh by clicking Refresh.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList className="bg-[#F5F1E8]">
                <TabsTrigger value="all" data-testid="lab-log-all">All ({status?.recent?.length || 0})</TabsTrigger>
                <TabsTrigger value="failed" data-testid="lab-log-failed">Failed ({(status?.recent || []).filter(r => r.status === "failed").length})</TabsTrigger>
              </TabsList>
              <TabsContent value="all"><SendLog rows={status?.recent || []} /></TabsContent>
              <TabsContent value="failed"><SendLog rows={(status?.recent || []).filter(r => r.status === "failed")} /></TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FieldRow({ label, value, setValue, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#5b5b56] font-mono mb-1.5">{label}</label>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="bg-[#F5F1E8] border-[#E5E0D2] focus-visible:ring-[#1A2DD7] focus-visible:ring-offset-0"
      />
    </div>
  );
}

function SendLog({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="py-6 text-center text-sm text-[#5b5b56]">No sends yet.</p>;
  }
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-[0.16em] text-[#5b5b56] font-mono">
          <th className="text-left px-2 py-2">When</th>
          <th className="text-left px-2 py-2">Recipient</th>
          <th className="text-left px-2 py-2">Type</th>
          <th className="text-left px-2 py-2">Status</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#E5E0D2]" data-testid={`lab-log-row-${r.id}`}>
              <td className="px-2 py-2 text-[#5b5b56] whitespace-nowrap text-xs">{new Date(r.created_at).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true })}</td>
              <td className="px-2 py-2">{r.recipient_email}</td>
              <td className="px-2 py-2 text-xs text-[#5b5b56]">{r.email_type === "kickoff_email" ? "Kickoff" : r.email_type === "followup_hub_email" ? "Follow-Up Hub" : "Workbook"}</td>
              <td className="px-2 py-2">
                {r.status === "sent" ? (
                  <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Sent</Badge>
                ) : (
                  <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50" title={r.error || ""}>Failed</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
